import { useQuery } from "@tanstack/react-query";
import { TableCard } from "ui-components/Layout";
import { ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { Fragment, useState } from "react";
import { useCustomerValue } from "providers/CustomerContext";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { CSVLink } from "react-csv";
import { Grid } from "@mui/material";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import dayjs from "dayjs";
import { Error_ } from "ui-components/Alert";
import { LoadingDialog } from "ui-components/Alert";
import { useTeamValue } from "providers/TeamContext";
import { getLAHistEnquiry } from "services/data.report.service";
import { laHistColumns } from "./columns";
import { ExportDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";

function LAHist() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const date = dayjs(new Date()).format("YYYY-MM-DD");
  const oneDaysBefore = dayjs(new Date()).subtract(1, 'day').format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(oneDaysBefore);
  const [endDate, setEndDate] = useState(date);
  const { downloadCsv, isDownloading } = useCsvDownloadHook();
  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };
  const { refetch, isFetching } = useQuery({
    queryKey: ["la-hist-enquiry", { customerCode }],
    queryFn: () => getLAHistEnquiry({ customerCode, startDate, endDate }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => {
      setData(data.data.data);
      setFilteredData(data.data.data);
    },
    onError: () => { },
  });
  const exportPermission =
    allowAll ||
    (Boolean(permissions["Historical Inventory_Report"]) &&
      permissions["Historical Inventory_Report"].findIndex((b) => b === "export") !== -1);

  const customProps = { startDate, setStartDate, endDate, setEndDate };

  const totals = filteredData.reduce(
    (acc, item) => {
      acc.totalQuantity += parseFloat(item.quantity || 0);
      if (item.locationID) acc.locations.add(item.locationID);
      const type = item.locationType || "No Type";
      acc.cubicByType[type] = (acc.cubicByType[type] || 0) + parseFloat(item.totalUPMCubic || 0);
      return acc;
    },
    { totalQuantity: 0, locations: new Set(), cubicByType: {} }
  );

  const { totalQuantity } = totals;
  const totalLocations = totals.locations.size;
  // Sort by location type, keeping "No Type" last
  const cubicByType = Object.entries(totals.cubicByType).sort(([a], [b]) =>
    a === "No Type" ? 1 : b === "No Type" ? -1 : a.localeCompare(b)
  );

  const onFetch = () => {
    setAlert(null);
    const valid =
      dayjs(endDate).isSame(dayjs(startDate)) || dayjs(endDate).isAfter(dayjs(startDate));
    if (!valid) {
      setAlert(<Error_ content="Start date cannot greater than end date" onChange={setAlert} />);
      return;
    }
    if (dayjs(date).isBefore(endDate)) {
      setAlert(<Error_ content="End date cannot greater than today date" onChange={setAlert} />);
      return;
    }
    refetch();
  };

  return (
    <ListLayout>
      {isFetching && <LoadingDialog />}
      {isDownloading && <ExportDialog />}
      {alert && <MDBox sx={{ my: 3 }}>{alert}</MDBox>}

      <TableCard sx={{ my: 3 }}>
        <MDBox margin="1rem 0 0 1rem">
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <CustomDateRangePicker {...customProps} />
            </Grid>
            <Grid item xs={4}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => {
                  onFetch();
                }}
              >
                search
              </MDButton>
              {/* <MDButton
                sx={{ marginLeft: "1rem" }}
                variant="gradient"
                color="info"
                disabled={!exportPermission}
              >
                <CSVLink style={{ color: "white" }} filename={`Historical_SKU_Movement`} data={filteredData}>
                  export
                </CSVLink>
              </MDButton> */}
              <MDButton
                sx={{ marginLeft: "1rem" }} variant="gradient" color="info" disabled={!exportPermission}
                onClick={() => {
                  const filename = `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_HistoricalSKUMovement`;
                  downloadCsv(filename, filteredData, null, [
                    ["Total Quantity", totalQuantity],
                    ["Total Locations", totalLocations],
                    ...cubicByType.map(([type, cubic]) => [`Total Cubic UPM (${type})`, cubic.toFixed(4)]),
                  ])
                }}
              >
                export
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>

        <BasicTable
          data={data}
          columns={laHistColumns}
          onFilterRowsChange={handleFilterRowsChange}
          caption={
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "24px",
                padding: "12px 16px",
                backgroundColor: "#f8f9fa",
                fontSize: "14px",
                color: "#666",
              }}
            >
              <div style={{ display: "flex", gap: "8px" }}>
                <span>Total Quantity:</span>
                <strong style={{ color: "#111" }}>{totalQuantity.toLocaleString()}</strong>
              </div>
              <div style={{ width: "1px", height: "14px", backgroundColor: "#ddd" }} />

              <div style={{ display: "flex", gap: "8px" }}>
                <span>Total Locations:</span>
                <strong style={{ color: "#1890ff" }}>{totalLocations.toLocaleString()}</strong>
              </div>

              {cubicByType.map(([type, cubic]) => (
                <Fragment key={type}>
                  <div style={{ width: "1px", height: "14px", backgroundColor: "#ddd" }} />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <span>Total Cubic UPM ({type}):</span>
                    <strong style={{ color: "#52c41a" }}>
                      {cubic.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                    </strong>
                  </div>
                </Fragment>
              ))}
            </div>
          }
        />
      </TableCard>
    </ListLayout>
  );
}

export default LAHist;
